/**
 * The camera, opened where iOS requires it to be opened: inside the tap.
 *
 * **This exists because of a bug that only ever appeared on a phone.** The scan flow used to leave
 * the camera to `browser-mindset-client.ts`, which asked for it inside `startPreviewMode` - after
 * `client.init()` had loaded 55 MB of vendor bundle, instantiated an 11 MB WASM module and started
 * MediaPipe. On desktop Chrome that is fine. On iOS every browser is WebKit, and WebKit requires
 * `getUserMedia` to be reached while the user activation from the tap is still live: seconds of
 * loading later it is long gone, so the call was refused *without the patient ever being asked*. The
 * reported symptom was exactly that - "got stuck, didn't ask for camera", on Safari and on Chrome for
 * iPhone alike, because Chrome for iPhone is WebKit too (docs/decisions.md D-24).
 *
 * So the stream is acquired first, in the handler for the patient's own tap, and handed to the vendor
 * client afterwards. The vendor gets a `<video>` that is already playing, which is what it wanted all
 * along: its frame pump reads `videoWidth`/`videoHeight` off the element and cares nothing for who
 * called `getUserMedia`.
 */

/**
 * What the scan asks the camera for.
 *
 * Moved here from `browser-mindset-client.ts` unchanged, and it is `ideal` throughout rather than
 * `exact` on purpose: an `exact` constraint a device cannot meet fails the whole call and the patient
 * gets no scan at all. A 30 fps scan of uncertain blood-pressure quality is a better answer to that
 * than none, and the SDK's own floor is 20.
 */
export const PATIENT_CAMERA: MediaStreamConstraints = {
  video: {
    facingMode: 'user',
    width: { ideal: 640 },
    height: { ideal: 480 },
    frameRate: { ideal: 60 },
  },
  audio: false,
}

/**
 * What a scan asks the camera for when TWO SDKs are reading it (docs/decisions.md D-38).
 *
 * **Thirty frames a second, not sixty, and the difference is the whole point.** Every frame the
 * camera delivers is a frame both engines process: Mindset pumps `requestVideoFrameCallback` -
 * `createImageBitmap` and a `detectVitalsJS` call per frame, plus MediaPipe segmentation - and Shen.AI
 * runs its own WebGL2 pipeline over the same stream. At 60 fps that is two full pipelines at twice the
 * rate on a phone already holding two WASM heaps, and it is what a real iPhone answered by killing the
 * tab mid-measurement.
 *
 * Nothing is measured less well for it. Mindset's own floor is 20 fps, Shen's presets are specified in
 * seconds rather than frames, and the vendors' own reference integrations ask for 30. Sixty was never
 * a requirement either SDK stated - it was the single-vendor path's `ideal`, where one engine had the
 * device to itself.
 *
 * `ideal` throughout for the reason above: an `exact` constraint a device cannot meet fails the whole
 * call, and a scan at whatever rate the camera can manage is a better answer than no scan.
 */
export const PATIENT_CAMERA_SHARED: MediaStreamConstraints = {
  video: {
    facingMode: 'user',
    width: { ideal: 640 },
    height: { ideal: 480 },
    frameRate: { ideal: 30 },
  },
  audio: false,
}

/** Whether this build carries the journey's camera-replacement shim. */
declare const __FH_CAMERA_REPLACEMENT__: boolean

declare global {
  interface Window {
    __VIDEO_URL?: string
  }
}

/**
 * Opens the patient's camera.
 *
 * MUST be called synchronously from a user gesture - see the note at the top of this file. Awaiting
 * anything before it on iOS turns a permission prompt into a silent refusal.
 *
 * @returns The stream, or null when this build is running a journey against recorded footage and
 * there is no camera to open.
 * @throws Whatever `getUserMedia` throws: a refusal, a device already in use, a browser with no
 * camera API at all. The caller turns that into the one sentence a patient reads.
 */
export async function openPatientCamera(
  /**
   * Whether this one stream is about to be read by TWO SDKs, which asks the camera for half the frame
   * rate - see `PATIENT_CAMERA_SHARED`. Defaults to the single-vendor constraints, so every existing
   * caller is unchanged.
   */
  options: {
    readonly sharedByTwoSdks?: boolean
    /**
     * The frame rate a SHARED stream asks for in place of `PATIENT_CAMERA_SHARED`'s 30 - a local
     * diagnostic (runtime-config.ts `hybridCameraFrameRate`): on an Android tablet Shen.AI measured
     * alone at 60 and never started beside Mindset at 30 (docs/decisions.md D-62).
     */
    readonly sharedFrameRate?: number
  } = {},
): Promise<MediaStream | null> {
  if (__FH_CAMERA_REPLACEMENT__ && window.__VIDEO_URL) {
    // The journey affordance: recorded footage stands in for the camera, so there is nothing to open
    // and nothing to be refused. Compiled out of the production bundle by the same define the two
    // vendor clients read.
    return null
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('This browser exposes no camera API')
  }

  if (!options.sharedByTwoSdks) {
    return navigator.mediaDevices.getUserMedia(PATIENT_CAMERA)
  }
  if (options.sharedFrameRate === undefined) {
    return navigator.mediaDevices.getUserMedia(PATIENT_CAMERA_SHARED)
  }
  const shared = PATIENT_CAMERA_SHARED.video as MediaTrackConstraints
  return navigator.mediaDevices.getUserMedia({
    ...PATIENT_CAMERA_SHARED,
    video: { ...shared, frameRate: { ideal: options.sharedFrameRate } },
  })
}

/**
 * A second handle on the SAME camera, for the second SDK of a hybrid scan (docs/decisions.md D-37).
 *
 * **One camera, two readers, and it must not be two `getUserMedia` calls.** A second call would be a
 * second device handle: on iOS it is refused outright once the tap's activation has expired (D-24),
 * on Android it commonly answers with a second stream at whatever settings the driver feels like,
 * and on a laptop it can simply fail because the device is already in use. None of that is a scan.
 * `MediaStream.clone()` is the operation that exists for this: it clones the TRACKS, and cloned
 * tracks share the one underlying source - so the camera is opened once, in the patient's tap, and
 * both SDKs read the same frames.
 *
 * **It is also what makes teardown safe.** Each vendor client stops the tracks of the stream it was
 * given (`destroy()`), and the device is released when the last track derived from the source stops.
 * Handing both halves the same object would mean the first `destroy()` blinding the vendor that was
 * still measuring; handing one a clone means each half can end on its own clock, which is exactly
 * what a hybrid scan does - it ends when the LAST vendor finishes.
 *
 * @param stream The camera opened in the tap, or null on the journey's recorded-footage path.
 * @returns An independent stream over the same source, or - on a browser too old to clone one - the
 * original, because a shared stream is a worse scan than no scan is a scan at all. Said out loud
 * when that happens: it makes the first teardown end the other half's frames, and that is a symptom
 * nobody would otherwise be able to explain.
 */
export function cloneCameraForSecondReader(stream: MediaStream | null): MediaStream | null {
  if (!stream) {
    return null
  }

  if (typeof stream.clone !== 'function') {
    console.warn('[camera] this browser cannot clone a stream, so both SDKs will share one handle')
    return stream
  }

  const clone = stream.clone()

  console.log('[camera] split for a second reader', {
    tracks: clone.getVideoTracks?.().length ?? 0,
  })

  return clone
}

/**
 * Puts a stream on a video element and starts it playing.
 *
 * **Called as soon as the scan screen exists, and not when the vendor finally asks for a preview.**
 * The camera is opened in the patient's tap (above), and the vendor client only reached
 * `startPreviewMode` after `init()` had loaded 55 MB of bundle and instantiated an 11 MB WASM module -
 * tens of seconds on a phone. A camera track held that long with nothing consuming it is what iOS
 * showed as a black preview: the permission had been granted, the stream was live, and no frame had
 * ever been asked for (docs/decisions.md D-25). Attaching immediately also means the patient sees
 * their own face while the vendor loads, instead of a black rectangle they cannot tell from a fault.
 *
 * Idempotent, because two callers now want the same thing: this one and the vendor client, which still
 * calls it in case it is driving a `<video>` nobody else attached.
 *
 * @param video The element the vendor will read frames off.
 * @param stream The camera, or null on the journey's recorded-footage path.
 */
export async function attachCameraToVideo(
  video: HTMLVideoElement,
  stream: MediaStream | null,
): Promise<void> {
  // Muted and inline BEFORE anything is attached: an unmuted video that autoplays is rejected by every
  // mobile browser's autoplay policy, and on iOS a video without `playsinline` is taken full-screen out
  // of the page the patient is looking at. Both are set as attributes as well as properties - React
  // sets `muted` as a property only, and WebKit reads the attribute when it decides whether a play()
  // needs a gesture.
  video.muted = true
  video.defaultMuted = true
  video.setAttribute('muted', '')
  video.setAttribute('playsinline', '')
  video.setAttribute('autoplay', '')

  if (stream && video.srcObject !== stream) {
    video.srcObject = stream
  }

  try {
    await video.play()
  } catch (error) {
    // A rejected play() is the difference between a black preview and a working one, and it is
    // invisible in every other way - so it is said out loud rather than swallowed. The scan is not
    // failed on it: the vendor reads frames off the element, and an element that started playing a
    // moment later still measures.
    console.warn('[camera] play() was refused', error)
  }

  // The state that explains a black preview, on the only device that can see it. Cheap, once per scan.
  console.log('[camera] attached', {
    paused: video.paused,
    readyState: video.readyState,
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
    tracks: stream?.getVideoTracks?.().map((track) => ({
      readyState: track.readyState,
      muted: track.muted,
      enabled: track.enabled,
      settings: track.getSettings?.(),
    })),
  })
}
